import { authedJson } from '../http';

export interface RiskLimitCore {
  state: string;
  numeric_state: string | null;
}

export interface RiskLimit extends RiskLimitCore {
  current_es_historical?: number | null;
  current_es_parametric?: number | null;
  current_es_used: number | null;
  current_es_limit: number | null;
  current_es_compounded_display?: number | null;
  exposure_ceiling: number | null;
  proportional_haircut?: number | null;
  tolerance?: number | null;
}

export interface RiskTheme {
  theme: string;
  signed_net_share: number | null;
  contribution_pp: number | null;
  hedged?: boolean;
}

export interface RiskPosition {
  symbol: string;
  instrument_id?: number | null;
  covered?: boolean;
  current_weight?: number | null;
  signed_net_share: number | null;
  contribution_pp: number | null;
  value_base?: number | null;
}

export interface RiskExcludedPosition {
  symbol: string;
  instrument_id?: number | null;
  reason: string;
  value_base?: number | null;
  available_bars?: number | null;
  required_bars?: number | null;
}

export interface RiskCoverage {
  state: string;
  excluded_state?: string;
  fx_state?: string;
  limit_scope?: string;
  covered_value_fraction?: number | null;
  excluded_value_fraction?: number | null;
  fx_immaterial?: boolean | null;
  fx_unadjusted_value_fraction?: number | null;
  excluded?: RiskExcludedPosition[];
}

export interface RiskEstimationQuality {
  tail_windows?: number | null;
  effective_tail_episodes?: number | null;
  short_tail?: boolean | null;
  parametric_floor_binding?: boolean | null;
  avg_correlation_available?: boolean | null;
  default_vol_count?: number | null;
  window_days?: number | null;
  covered_instruments?: number;
  held_instruments?: number;
}

interface RiskMonitorBase {
  engine_version: string;
  computed_at: string;
  as_of?: string | null;
  base_currency?: string | null;
  positions_value?: number | null;
  portfolio_limit: RiskLimitCore;
  contribution_basis?: string | null;
  estimation_quality?: RiskEstimationQuality | null;
  theme_limits: { state: string; themes: RiskTheme[] };
  position_policy: {
    state: string;
    breaches?: {
      symbol: string;
      current_weight: number;
      limit: number;
    }[];
  };
  coverage: RiskCoverage;
}

export interface RiskMonitorAvailable extends RiskMonitorBase {
  portfolio_limit: RiskLimit;
  degraded?: false;
}

export interface RiskMonitorDegraded extends RiskMonitorBase {
  degraded: true;
  degraded_reason: string | null;
}

export type RiskMonitor = RiskMonitorAvailable | RiskMonitorDegraded;

export type PositionRiskMonitor = RiskMonitor & { position: RiskPosition };

export function getRiskMonitor(): Promise<RiskMonitor> {
  return authedJson('GET', '/api/v1/portfolio/risk/monitor');
}

export function getPositionRisk(positionId: number): Promise<PositionRiskMonitor> {
  return authedJson('GET', `/api/v1/positions/${positionId}/risk`);
}
